import { Button, ButtonGroup, Typography } from '@statsbomb/kitbag-components';
import { Gender } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { genderAtom, settingsConfigAtom } from '@/atoms/queries/userConfigs';
import { useOptimisticUserConfigUpdate } from '@/hooks/useOptimisticUserConfigUpdate';
import { UserConfigPutMutationParams, UserConfigSettings, UserConfigType } from '@/types/userConfigs';

interface GenderToggleProps {
  isLoading?: boolean;
  gender?: Gender;
  settingsConfig?: UserConfigSettings | null;
}

const GenderToggleBase = ({ isLoading = false, gender, settingsConfig }: GenderToggleProps) => {
  const { t } = useTranslation('preferences');

  const { updateUserConfigOptimistically, localValue } = useOptimisticUserConfigUpdate(gender, 'updateGender');

  const isMen = !localValue || localValue === Gender.M;

  const saveGender = async (newGender: Gender) => {
    const existingDefinition = settingsConfig?.definition;
    const mutationParams: UserConfigPutMutationParams = {
      configId: settingsConfig?.configId,
      configType: UserConfigType.SETTINGS,
      configDefinition: {
        ...existingDefinition,
        gender: newGender,
      },
    };

    await updateUserConfigOptimistically(mutationParams, newGender);
  };

  return (
    <div>
      <Typography variant="headingMedium">{t('data.gender.label')}</Typography>
      <ButtonGroup>
        <Button
          onClick={() => saveGender(Gender.M)}
          shape="pill"
          variant={isMen ? 'primary' : 'ghost'}
          disabled={isLoading}
        >
          {t('data.gender.m')}
        </Button>
        <Button
          onClick={() => saveGender(Gender.F)}
          shape="pill"
          variant={isMen ? 'ghost' : 'primary'}
          disabled={isLoading}
        >
          {t('data.gender.w')}
        </Button>
      </ButtonGroup>
    </div>
  );
};

const GenderToggleWithData = () => {
  const settingsConfig = useAtomValue(settingsConfigAtom);
  const gender = useAtomValue(genderAtom);

  return <GenderToggleBase settingsConfig={settingsConfig} gender={gender} />;
};

export const GenderToggle = () => (
  <Suspense fallback={<GenderToggleBase isLoading />}>
    <GenderToggleWithData />
  </Suspense>
);
